import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../store';
import { GAME_DB } from '../../db/game';
import { setGameTasks, setTileCacheStatus, setTileCacheProgress } from '../../store/slices/rallyGame';
import { APP_ROUTES } from '../../app.config.vars/routes';
import { getGameTasks } from '../../api/game-mode/game';
import { useTranslation } from 'react-i18next';
import { API_ERRORS } from '../../app.config.vars/errorCodes';
import { setIsModalOpen, setModalContent } from '../../store/slices/global';
import { MODAL_CONTENT } from '../../app.config.vars/modalContent';
import { calculateBoundingBox, downloadTilesForRally, cacheMapAssets, getTileCacheStatus } from '../../helpers/mapTileCaching';
import { useSelector } from 'react-redux';
import { selectRallyInfo, selectTileCacheStatus } from '../../store/slices/rallyGame';
import pinIcon from '../../assets/icons/pin-map.png';
import targetPinIcon from '../../assets/icons/target-pin-map.png';
const useFetchGameTasks = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const rallyInfo = useSelector(selectRallyInfo);
    const tileCacheStatus = useSelector(selectTileCacheStatus);
    useEffect(() => {
        const fetchGameTasks = async () => {
            try {
                const gameTasks = await getGameTasks();
                if (gameTasks.data && gameTasks.data.length) {
                    await GAME_DB.SET.gameTasks(gameTasks.data);
                    dispatch(setGameTasks(gameTasks.data));
                    // Download map tiles for check-in tasks in the background
                    if (rallyInfo?.uid) {
                        const bounds = calculateBoundingBox(gameTasks.data);
                        if (bounds) {
                            // Skip if already downloading
                            if (tileCacheStatus === 'downloading') {
                                console.log('[Tile Caching] Download already in progress, skipping');
                            }
                            else {
                                // Check if tiles are already cached
                                const cacheStatus = await getTileCacheStatus(rallyInfo.uid);
                                if (cacheStatus.isCached) {
                                    console.log('[Tile Caching] Tiles already cached for this rally, skipping download');
                                    dispatch(setTileCacheStatus('cached'));
                                    dispatch(setTileCacheProgress(100));
                                }
                                else {
                                    console.log('[Tile Caching] Bounding box calculated:', bounds);
                                    dispatch(setTileCacheStatus('downloading'));
                                    dispatch(setTileCacheProgress(0));
                                    // Cache map marker assets
                                    cacheMapAssets([
                                        { key: 'pin-map', url: pinIcon },
                                        { key: 'target-pin-map', url: targetPinIcon },
                                        { key: 'marker-shadow', url: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png' },
                                    ]).catch((error) => {
                                        console.error('[Asset Caching] Failed to cache assets:', error);
                                    });
                                    // Download tiles in background (non-blocking)
                                    downloadTilesForRally(rallyInfo.uid, bounds, [16, 17, 18, 19], // 3 zoom levels as per user preference
                                    'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', (progress) => {
                                        dispatch(setTileCacheProgress(progress.percentage));
                                        console.log(`[Tile Caching] Progress: ${progress.percentage}%`);
                                    })
                                        .then((result) => {
                                        if (result.success) {
                                            dispatch(setTileCacheStatus('cached'));
                                            dispatch(setTileCacheProgress(100));
                                            console.log('[Tile Caching] All tiles cached successfully');
                                        }
                                        else {
                                            dispatch(setTileCacheStatus('failed'));
                                            console.warn('[Tile Caching] Some tiles failed to download');
                                        }
                                    })
                                        .catch((error) => {
                                        dispatch(setTileCacheStatus('failed'));
                                        console.error('[Tile Caching] Error during tile download:', error);
                                    });
                                }
                            }
                        }
                        else {
                            console.log('[Tile Caching] No check-in tasks with coordinates found');
                        }
                    }
                }
                else if (gameTasks.error?.code === API_ERRORS.noInternet) {
                    const gameTasks = await GAME_DB.GET.gameTasks();
                    if (gameTasks) {
                        dispatch(setGameTasks(gameTasks));
                    }
                    else {
                        dispatch(setIsModalOpen(true));
                        dispatch(setModalContent(MODAL_CONTENT.somethingWentWrong.key));
                    }
                }
                else {
                    console.log('No access to play game');
                    navigate(APP_ROUTES.rallies);
                }
            }
            catch (error) {
                dispatch(setIsModalOpen(true));
                dispatch(setModalContent(MODAL_CONTENT.somethingWentWrong.key));
            }
        };
        fetchGameTasks();
    }, [i18n.language]);
};
export default useFetchGameTasks;
